"use client";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";
import { useUIStore } from "@/store/uiStore";
import {
   ChevronLeft,
   ChevronRight,
   Fish,
   FolderOpen,
   LayoutDashboard,
   LogOut,
   Package,
   Settings,
   ShoppingCart,
   X,
} from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect } from "react";

const navigation = [
   { name: "Дашборд", href: "/", icon: LayoutDashboard },
   { name: "Заказы", href: "/orders", icon: ShoppingCart },
   { name: "Товары", href: "/products", icon: Package },
   { name: "Категории", href: "/categories", icon: FolderOpen },
   { name: "Настройки", href: "/settings", icon: Settings },
];

export function Sidebar() {
   const pathname = usePathname();
   const router = useRouter();
   const logout = useAuthStore((state) => state.logout);
   const admin = useAuthStore((state) => state.admin);

   const isMobileSidebarOpen = useUIStore((state) => state.isMobileSidebarOpen);
   const closeMobileSidebar = useUIStore((state) => state.closeMobileSidebar);
   const isSidebarCollapsed = useUIStore((state) => state.isSidebarCollapsed);
   const toggleSidebarCollapsed = useUIStore(
      (state) => state.toggleSidebarCollapsed,
   );

   // Close mobile sidebar on route change
   useEffect(() => {
      closeMobileSidebar();
   }, [pathname, closeMobileSidebar]);

   // Close mobile sidebar on ESC key
   useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
         if (e.key === "Escape") {
            closeMobileSidebar();
         }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
   }, [closeMobileSidebar]);

   const handleLogout = () => {
      closeMobileSidebar();
      logout();
      router.push("/login");
   };

   return (
      <>
         {/* Backdrop overlay for mobile/tablet */}
         {isMobileSidebarOpen && (
            <div
               onClick={closeMobileSidebar}
               aria-hidden="true"
               className="fixed inset-0 z-40 bg-black/60 backdrop-blur-xs transition-opacity duration-300 lg:hidden"
            />
         )}

         {/* Sidebar / Drawer */}
         <aside
            className={cn(
               "fixed left-0 top-0 z-50 h-screen border-r border-[hsl(var(--border))] bg-[hsl(var(--card))] transition-all duration-300 ease-in-out flex flex-col",
               // Mobile styles: slide out drawer
               "w-72 max-w-[85vw] -translate-x-full lg:translate-x-0",
               isMobileSidebarOpen && "translate-x-0 shadow-2xl",
               // Desktop collapsed / expanded styles
               isSidebarCollapsed ? "lg:w-20" : "lg:w-64",
            )}
         >
            {/* Logo and close button */}
            <div className="flex h-16 items-center justify-between px-4 border-b border-[hsl(var(--border))]">
               <Link
                  href="/"
                  onClick={closeMobileSidebar}
                  className="flex items-center gap-3"
               >
                  <div className="w-10 h-10 gradient-primary rounded-xl flex items-center justify-center shadow-lg shrink-0">
                     <Fish size={22} className="text-white" />
                  </div>
                  {(!isSidebarCollapsed || isMobileSidebarOpen) && (
                     <div className="animate-fade-in truncate">
                        <h1 className="text-lg font-bold text-gradient">
                           Topfish
                        </h1>
                        <p className="text-[10px] text-[hsl(var(--muted-foreground))] -mt-0.5">
                           Админ-панель
                        </p>
                     </div>
                  )}
               </Link>

               {/* Mobile close button */}
               <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeMobileSidebar}
                  aria-label="Закрыть меню"
                  className="lg:hidden text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
               >
                  <X size={20} />
               </Button>
            </div>

            {/* Navigation */}
            <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
               {navigation.map((item) => {
                  const isActive =
                     pathname === item.href ||
                     (item.href !== "/" && pathname.startsWith(item.href));

                  return (
                     <Link
                        key={item.name}
                        href={item.href}
                        onClick={closeMobileSidebar}
                        className={cn(
                           "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                           isActive
                              ? "gradient-primary text-black font-semibold shadow-lg"
                              : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent)/0.1)]",
                        )}
                     >
                        <item.icon size={20} className="shrink-0" />
                        {(!isSidebarCollapsed || isMobileSidebarOpen) && (
                           <span className="font-medium animate-fade-in truncate">
                              {item.name}
                           </span>
                        )}
                     </Link>
                  );
               })}
            </nav>

            {/* Bottom section */}
            <div className="p-4 border-t border-[hsl(var(--border))] space-y-2">
               {/* User info */}
               {admin && (!isSidebarCollapsed || isMobileSidebarOpen) && (
                  <div className="px-4 py-2 text-sm text-[hsl(var(--muted-foreground))] animate-fade-in">
                     <span className="block font-medium text-[hsl(var(--foreground))] truncate">
                        {admin.username}
                     </span>
                  </div>
               )}

               {/* Logout button */}
               <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className={cn(
                     "w-full text-red-500 hover:text-red-600 hover:bg-red-500/10",
                     isSidebarCollapsed && !isMobileSidebarOpen
                        ? "justify-center px-0"
                        : "justify-start gap-3",
                  )}
               >
                  <LogOut size={18} className="shrink-0" />
                  {(!isSidebarCollapsed || isMobileSidebarOpen) && (
                     <span>Выйти</span>
                  )}
               </Button>

               {/* Collapse toggle (desktop only) */}
               <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSidebarCollapsed}
                  className="hidden lg:flex w-full justify-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
               >
                  {isSidebarCollapsed ? (
                     <ChevronRight size={18} />
                  ) : (
                     <ChevronLeft size={18} />
                  )}
               </Button>
            </div>
         </aside>
      </>
   );
}
